import type {
  CampStatus,
  ActivityType,
  TaskStatus,
  ParticipantStatus,
  QuoteCategory,
  ExpenseCategory,
} from '@/types/database.ts'

// --- Status Labels ---

/** 案件の段の順番（画面の並び・進め方の正本）。 */
export const CAMP_STATUS_ORDER: CampStatus[] = [
  'inquiry',
  'negotiating',
  'likely',
  'confirmed',
  'active',
  'settling',
  'settled',
  'completed',
]

// 8 段にする前の値（サーバが寄せ終わるまで DB に残る・旧い画面が書きうる）。
const LEGACY_CAMP_STATUS: Record<string, CampStatus> = {
  draft: 'inquiry',
  planning: 'negotiating',
  quoting: 'negotiating',
  deposit: 'confirmed',
}

/** DB から来た状態を 8 段のどれかにする。知らない値は「問い合わせあり」。 */
export function normalizeCampStatus(status: string | null | undefined): CampStatus {
  if (status && (CAMP_STATUS_ORDER as string[]).includes(status)) return status as CampStatus
  return (status && LEGACY_CAMP_STATUS[status]) || 'inquiry'
}

/** まだ実施が決まっていない段（集計で「動いている案件」から外す）。 */
export const PRE_SALES_STATUSES: CampStatus[] = ['inquiry', 'negotiating']

export const STATUS_LABELS: Record<CampStatus, string> = {
  inquiry: '問い合わせあり',
  negotiating: '具体商談中',
  likely: '実施濃厚',
  confirmed: '確定・旅前',
  active: '旅中',
  settling: 'ツアー完了（精算中）',
  settled: '精算完了・最終確認',
  completed: '完了',
}

export const STATUS_COLORS: Record<CampStatus, string> = {
  inquiry: 'bg-gray-100 text-gray-700',
  negotiating: 'bg-blue-100 text-blue-700',
  likely: 'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-green-100 text-green-700',
  active: 'bg-emerald-100 text-emerald-700',
  settling: 'bg-orange-100 text-orange-700',
  settled: 'bg-sky-100 text-sky-700',
  completed: 'bg-slate-100 text-slate-700',
}

// --- Activity Type Labels ---

export const ACTIVITY_TYPE_LABELS: Record<ActivityType, { label: string; icon: string }> = {
  transport: { label: '移動', icon: 'Bus' },
  accommodation: { label: '宿泊', icon: 'Hotel' },
  content: { label: 'コンテンツ', icon: 'Star' },
  meal: { label: '食事', icon: 'UtensilsCrossed' },
  free: { label: '自由行動', icon: 'Clock' },
}

export const ACTIVITY_TYPE_COLORS: Record<ActivityType, string> = {
  transport: 'bg-sky-100 text-sky-700',
  accommodation: 'bg-purple-100 text-purple-700',
  content: 'bg-amber-100 text-amber-700',
  meal: 'bg-rose-100 text-rose-700',
  free: 'bg-teal-100 text-teal-700',
}

// --- Task Status Labels ---

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  todo: '未着手',
  in_progress: '進行中',
  review: 'レビュー',
  done: '完了',
}

// --- Participant Status Labels ---

export const PARTICIPANT_STATUS_LABELS: Record<ParticipantStatus, string> = {
  interested: '関心あり',
  registered: '登録済み',
  paid_deposit: '手付金済み',
  confirmed: '確定',
}

// --- Quote Category Labels ---

export const CATEGORY_LABELS: Record<QuoteCategory, string> = {
  accommodation: '宿泊',
  transport: '交通',
  content: 'コンテンツ',
  meal: '食事',
  pm_fee: 'PM費',
  coach_fee: 'コーチ費',
  other: 'その他',
}

export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  accommodation: '宿泊',
  transport: '交通',
  content: 'コンテンツ',
  meal: '食事',
  pm_fee: 'PM費',
  coach_fee: 'コーチ費',
  labor: '人件費',
  other: 'その他',
}

// --- Format Functions ---

export function formatCurrency(amount: number): string {
  return `\u00a5${amount.toLocaleString('ja-JP')}`
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function formatDateRange(start: string, end: string): string {
  const startDate = new Date(start)
  const endDate = new Date(end)

  const startStr = startDate.toLocaleDateString('ja-JP', {
    month: 'long',
    day: 'numeric',
  })
  const endStr = endDate.toLocaleDateString('ja-JP', {
    month: 'long',
    day: 'numeric',
  })

  return `${startStr} - ${endStr}`
}
